import { useState, useEffect } from "react";
import { useAuth } from "../lib/AuthContext";
import api, { subscriptionApi } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import DowngradeWarningDialog from "../components/DowngradeWarningDialog";
import PlanComparisonTable from "../components/PlanComparisonTable";
import { 
  CreditCard, 
  Users, 
  ExternalLink, 
  Loader2, 
  CheckCircle2,
  Clock,
  Receipt,
  Settings,
  ArrowUpRight,
  Check,
  Download,
  FileText,
  ChevronDown
} from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";

const Billing = () => {
  const { company, trialDaysRemaining } = useAuth();
  const [billingInfo, setBillingInfo] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [packages, setPackages] = useState([]);
  const [currencySymbol, setCurrencySymbol] = useState("£");
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);
  const [downloadingInvoice, setDownloadingInvoice] = useState(null);
  const [showComparison, setShowComparison] = useState(false);
  
  // Downgrade warning state
  const [showDowngradeWarning, setShowDowngradeWarning] = useState(false);
  const [pendingPackage, setPendingPackage] = useState(null);
  const [affectedFeatures, setAffectedFeatures] = useState([]);
  const [cleanupLoading, setCleanupLoading] = useState(false);

  useEffect(() => {
    const fetchBillingData = async () => {
      try {
        // Get company's currency
        const currency = company?.currency || "gbp";
        
        const [billingRes, historyRes, packagesRes] = await Promise.all([
          api.get("/billing/subscription"),
          api.get("/payments/history"),
          api.get(`/packages?currency=${currency}`)
        ]);
        setBillingInfo(billingRes.data);
        setPaymentHistory(historyRes.data.transactions || []);
        setPackages(packagesRes.data.packages || []);
        
        // Set currency symbol based on packages response or company currency
        const symbols = { gbp: "£", eur: "€", usd: "$" };
        setCurrencySymbol(symbols[currency] || "£");
      } catch (error) {
        console.error("Error fetching billing data:", error);
        toast.error("Failed to load billing information");
      } finally {
        setLoading(false);
      }
    };

    fetchBillingData();
  }, [company?.currency]);

  const handleOpenPortal = async () => {
    setPortalLoading(true);
    try {
      const response = await api.post("/billing/portal", {
        return_url: `${window.location.origin}/dashboard/settings`
      });
      
      if (response.data.portal_url) {
        window.location.href = response.data.portal_url;
      } else {
        toast.error("Failed to open billing portal");
      }
    } catch (error) {
      console.error("Error opening portal:", error);
      const message = error.response?.data?.detail || "Failed to open billing portal";
      toast.error(message);
    } finally {
      setPortalLoading(false);
    }
  };

  const handleDownloadInvoice = async (transactionId) => {
    setDownloadingInvoice(transactionId);
    try {
      const response = await api.get(`/payments/invoice/${transactionId}`);
      
      if (response.data.invoice_pdf) {
        // Open the PDF in a new tab
        window.open(response.data.invoice_pdf, '_blank');
      } else if (response.data.hosted_invoice_url) {
        // Fallback to hosted invoice page
        window.open(response.data.hosted_invoice_url, '_blank');
      } else {
        toast.error("Invoice PDF not available");
      }
    } catch (error) {
      console.error("Error downloading invoice:", error);
      const message = error.response?.data?.detail || "Failed to download invoice";
      toast.error(message);
    } finally {
      setDownloadingInvoice(null);
    }
  };

  const handleUpgrade = async (packageId) => {
    try {
      // Find the target package
      const targetPackage = packages.find(p => p.id === packageId);
      if (!targetPackage) {
        toast.error("Package not found");
        return;
      }
      
      // Get current feature usage
      const usageResponse = await subscriptionApi.getFeatureUsage();
      const usedFeatures = usageResponse.data.used_features || [];
      
      // Check if any used features are NOT available in the target package
      const targetFeatureFlags = targetPackage.feature_flags || {};
      const featuresAtRisk = usedFeatures.filter(feature => 
        targetFeatureFlags[feature.feature_key] !== true
      );
      
      if (featuresAtRisk.length > 0) {
        // Show warning dialog
        setPendingPackage(targetPackage);
        setAffectedFeatures(featuresAtRisk);
        setShowDowngradeWarning(true);
        return;
      }
      
      // No features at risk, proceed with checkout
      await proceedToCheckout(packageId);
    } catch (error) {
      console.error("Error checking feature usage:", error);
      // If feature check fails, proceed anyway (fail open)
      await proceedToCheckout(packageId);
    }
  };

  const proceedToCheckout = async (packageId) => {
    try {
      const response = await api.post("/checkout/create-session", {
        package_id: packageId,
        origin_url: window.location.origin
      });
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      console.error("Error creating checkout:", error);
      toast.error(error.response?.data?.detail || "Failed to start checkout");
    }
  };

  const handleConfirmDowngrade = async () => {
    if (!pendingPackage) return;
    
    setCleanupLoading(true);
    try {
      // Clean up the data for affected features
      const featureKeys = affectedFeatures.map(f => f.feature_key);
      await subscriptionApi.cleanupFeatureData(featureKeys);
      
      toast.success("Data cleaned up successfully");
      
      // Close dialog and proceed to checkout
      setShowDowngradeWarning(false);
      await proceedToCheckout(pendingPackage.id);
    } catch (error) {
      console.error("Error cleaning up data:", error);
      toast.error("Failed to clean up data. Please try again.");
    } finally {
      setCleanupLoading(false);
    }
  };

  const handleCancelDowngrade = () => {
    setShowDowngradeWarning(false);
    setPendingPackage(null);
    setAffectedFeatures([]);
  };

  // Subscription status flags
  const isOnTrial = billingInfo?.subscription_status === "trial";
  const hasActivePlan = billingInfo?.subscription_status === "active" && billingInfo?.subscription_tier;
  const isTrialOrFree = isOnTrial || !billingInfo?.subscription_tier;

  // Get subscription packages from API
  const subscriptionPackages = packages.filter(p => p.type === "subscription");
  
  // Find current plan from packages
  const currentPlan = hasActivePlan 
    ? subscriptionPackages.find(p => p.tier === billingInfo?.subscription_tier)
    : null;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Billing & Subscription</h1>
        <p className="text-muted-foreground">Manage your subscription and payment methods</p>
      </div>

      {/* Current Plan Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="w-5 h-5" />
            {isOnTrial ? "Trial Status" : "Current Plan"}
          </CardTitle>
          <CardDescription>
            {isOnTrial ? "You're currently on a free trial" : "Your active subscription details"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                  billingInfo?.subscription_status === "active" 
                    ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400"
                    : billingInfo?.subscription_status === "trial"
                    ? "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
                    : "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400"
                }`}>
                  {billingInfo?.subscription_status === "active" && <CheckCircle2 className="w-3.5 h-3.5 inline mr-1" />}
                  {billingInfo?.subscription_status === "trial" && <Clock className="w-3.5 h-3.5 inline mr-1" />}
                  {billingInfo?.subscription_status?.charAt(0).toUpperCase() + billingInfo?.subscription_status?.slice(1) || "Free"}
                </div>
                <span className="text-2xl font-bold">
                  {isOnTrial ? "Free Trial" : (currentPlan ? currentPlan.name : "No Plan")}
                </span>
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                {hasActivePlan && currentPlan && (
                  <div>
                    <p className="text-muted-foreground">Monthly Price</p>
                    <p className="font-semibold text-lg">
                      {currencySymbol}{currentPlan.price.toFixed(2)}
                    </p>
                  </div>
                )}
                {isOnTrial && (
                  <>
                    <div>
                      <p className="text-muted-foreground">Trial Ends In</p>
                      <p className="font-semibold text-lg text-amber-600 dark:text-amber-400">
                        {trialDaysRemaining} days
                      </p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Access Level</p>
                      <p className="font-semibold text-lg">
                        Full Features
                      </p>
                    </div>
                  </>
                )}
                {billingInfo?.additional_employees > 0 && (
                  <div>
                    <p className="text-muted-foreground">Additional Staff</p>
                    <p className="font-semibold text-lg flex items-center gap-1">
                      <Users className="w-4 h-4" />
                      +{billingInfo.additional_employees}
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-col gap-2">
              {billingInfo?.has_stripe_customer && !isOnTrial && (
                <Button 
                  onClick={handleOpenPortal}
                  disabled={portalLoading}
                  variant="outline"
                  data-testid="manage-billing-btn"
                >
                  {portalLoading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Settings className="w-4 h-4 mr-2" />
                  )}
                  Manage Billing
                  <ExternalLink className="w-3.5 h-3.5 ml-2" />
                </Button>
              )}
              {isTrialOrFree && (
                <Button 
                  onClick={() => handleUpgrade("standard_monthly")}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="upgrade-plan-btn"
                >
                  Upgrade Plan
                  <ArrowUpRight className="w-4 h-4 ml-2" />
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Available Plans */}
      <Card>
        <CardHeader>
          <CardTitle>{isOnTrial ? "Choose a Plan" : "Available Plans"}</CardTitle>
          <CardDescription>
            {isOnTrial 
              ? "Select a plan to continue after your trial ends" 
              : "Choose the plan that fits your needs"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-4">
            {subscriptionPackages.map((pkg) => {
              // Only mark as current if user has an ACTIVE paid subscription for this tier
              const isCurrent = hasActivePlan && billingInfo?.subscription_tier === pkg.tier;
              const isPopular = pkg.tier === "standard";
              
              return (
                <Card 
                  key={pkg.id} 
                  className={`relative ${isCurrent ? "border-blue-500 ring-1 ring-blue-500" : isPopular && isOnTrial ? "border-blue-300" : ""}`}
                >
                  {isCurrent && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-blue-600 text-white text-xs rounded-full">
                      Current Plan
                    </div>
                  )}
                  {isPopular && isOnTrial && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-blue-600 text-white text-xs rounded-full">
                      Most Popular
                    </div>
                  )}
                  <CardContent className="p-4 pt-6">
                    <div className="text-center space-y-2">
                      <h3 className="font-semibold text-lg">{pkg.name}</h3>
                      <p className="text-3xl font-bold">
                        {currencySymbol}{pkg.price}
                        <span className="text-sm font-normal text-muted-foreground">/mo</span>
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {pkg.max_employees === -1 ? "Unlimited" : `Up to ${pkg.max_employees}`} employees
                      </p>
                    </div>
                    
                    {/* Features list */}
                    {pkg.features && pkg.features.length > 0 && (
                      <ul className="mt-4 space-y-2">
                        {pkg.features.slice(0, 4).map((feature, idx) => (
                          <li key={idx} className="flex items-center gap-2 text-sm">
                            <Check className="w-4 h-4 text-blue-600 flex-shrink-0" />
                            <span className="text-muted-foreground">{feature}</span>
                          </li>
                        ))}
                        {pkg.features.length > 4 && (
                          <li className="text-sm text-muted-foreground pl-6">
                            +{pkg.features.length - 4} more
                          </li>
                        )}
                      </ul>
                    )}
                    
                    <div className="mt-4">
                      {isCurrent ? (
                        <Button className="w-full" variant="outline" disabled>
                          Current Plan
                        </Button>
                      ) : (
                        <Button 
                          className="w-full" 
                          variant={isPopular ? "default" : "outline"}
                          onClick={() => handleUpgrade(pkg.id)}
                        >
                          {isOnTrial ? "Select Plan" : 
                           hasActivePlan && currentPlan && currentPlan.price > pkg.price 
                             ? "Downgrade" : "Upgrade"}
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Compare Features Button */}
          <div className="text-center mt-6">
            <Button
              variant="ghost"
              onClick={() => setShowComparison(!showComparison)}
              className="text-muted-foreground hover:text-foreground"
            >
              {showComparison ? "Hide comparison" : "Compare all features"}
              <ChevronDown className={`w-4 h-4 ml-2 transition-transform duration-300 ${showComparison ? "rotate-180" : ""}`} />
            </Button>
          </div>

          {/* Feature Comparison Table */}
          {showComparison && (
            <div className="mt-6 p-4 bg-muted/30 rounded-lg">
              <PlanComparisonTable 
                packages={packages}
                currencySymbol={currencySymbol}
                showPrices={false}
                highlightTier={billingInfo?.subscription_tier || "standard"}
                variant="compact"
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Payment History */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Receipt className="w-5 h-5" />
            Payment History
          </CardTitle>
          <CardDescription>Your recent transactions</CardDescription>
        </CardHeader>
        <CardContent>
          {paymentHistory.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Receipt className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No payment history yet</p>
              <p className="text-sm mt-1">Your transactions will appear here after making a purchase</p>
            </div>
          ) : (
            <div className="space-y-3">
              {paymentHistory.slice(0, 5).map((transaction) => (
                <div 
                  key={transaction.id}
                  className="flex items-center justify-between p-4 rounded-lg bg-muted/50"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      transaction.payment_status === "paid"
                        ? "bg-emerald-100 dark:bg-emerald-900/30"
                        : "bg-slate-100 dark:bg-slate-800"
                    }`}>
                      {transaction.payment_status === "paid" ? (
                        <CheckCircle2 className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                      ) : (
                        <Clock className="w-5 h-5 text-slate-600 dark:text-slate-400" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium">{transaction.package_name}</p>
                      <p className="text-sm text-muted-foreground">
                        {transaction.created_at && format(new Date(transaction.created_at), "MMM d, yyyy 'at' h:mm a")}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="font-semibold">{currencySymbol}{transaction.amount?.toFixed(2)}</p>
                      <p className={`text-xs ${
                        transaction.payment_status === "paid"
                          ? "text-emerald-600 dark:text-emerald-400"
                          : "text-muted-foreground"
                      }`}>
                        {transaction.payment_status?.charAt(0).toUpperCase() + transaction.payment_status?.slice(1)}
                      </p>
                    </div>
                    {transaction.payment_status === "paid" && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDownloadInvoice(transaction.id)}
                        disabled={downloadingInvoice === transaction.id}
                        className="text-muted-foreground hover:text-foreground"
                        title="Download Invoice"
                        data-testid={`download-invoice-${transaction.id}`}
                      >
                        {downloadingInvoice === transaction.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Download className="w-4 h-4" />
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Downgrade Warning Dialog */}
      <DowngradeWarningDialog
        open={showDowngradeWarning}
        onOpenChange={setShowDowngradeWarning}
        currentPlan={billingInfo?.subscription_status === "trial" ? "Trial" : (billingInfo?.subscription_tier || "Free")}
        targetPlan={pendingPackage}
        affectedFeatures={affectedFeatures}
        onConfirm={handleConfirmDowngrade}
        onCancel={handleCancelDowngrade}
        loading={cleanupLoading}
      />
    </div>
  );
};

export default Billing;
